import { Link } from "react-router-dom";
import { ArrowRight, Heart, Users, Check } from "lucide-react";
import { usePageMeta } from "../hooks/usePageMeta";

/**
 * Two-card splash page that routes to the correct sign-up form based on
 * whether the visitor is a patient or a caregiver.
 *
 * Layout: alternating dark + light cards with numbered eyebrows, value
 * bullets and full-width lime CTAs. Designed to feel intentional rather
 * than utilitarian.
 */
export default function RegisterPage() {
  usePageMeta({
    path: "/register",
    title: "Register with MediTalk , Join as a patient or caregiver",
    description:
      "Create your free MediTalk account in 3 minutes. Patients and caregivers, share your experience with healthcare research and get paid for your time.",
  });
  return (
    <div className="min-h-screen">
      {/* Hero — DARK */}
      <section className="bg-[var(--dark-bg)] grain-dark relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute -top-32 -left-24 w-[420px] h-[420px] rounded-full bg-[var(--lime)]/12 blur-3xl pointer-events-none"
        />
        <div className="container-main py-16 md:py-20 relative z-10">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[var(--lime)] mb-4">
              <span className="w-8 h-px bg-[var(--lime)]" />
              Join MediTalk
            </span>
            <h1 className="font-['Outfit'] font-bold text-white tracking-tight text-4xl md:text-5xl lg:text-6xl mb-5 leading-[1.05]">
              Join the community in
              <span className="text-[var(--lime)]"> 3 minutes.</span>
            </h1>
            <p className="text-lg text-[var(--gray-light)] leading-relaxed max-w-xl">
              We'll only get in touch when there's a study that matches you,
              never before. Your information is private and you can withdraw
              at any time.
            </p>
          </div>
        </div>
      </section>

      {/* Choice — LIGHT */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-main">
          <div className="max-w-2xl mb-12 md:mb-14">
            <h2 className="font-['Outfit'] font-bold text-[var(--charcoal)] text-3xl md:text-4xl leading-tight mb-3">
              Which one is{" "}
              <span
                className="px-1.5 box-decoration-clone"
                style={{
                  backgroundImage:
                    "linear-gradient(180deg, transparent 0 55%, var(--lime) 55% 92%, transparent 92% 100%)",
                }}
              >
                you?
              </span>
            </h2>
            <p className="text-[var(--text-muted)] text-lg leading-relaxed">
              Pick the path that fits your story. Each opens a short
              registration designed around the people taking it.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            <PatientCard />
            <CaregiverCard />
          </div>

          <p className="text-center text-sm text-[var(--text-muted)] mt-12">
            Already registered?{" "}
            <Link
              to="/account/request-link"
              className="text-[var(--charcoal)] hover:text-[var(--dark-bg)] font-semibold underline decoration-[var(--lime)] decoration-2 underline-offset-4"
              data-testid="update-details-link"
            >
              Update your details
            </Link>
            .
          </p>
        </div>
      </section>
    </div>
  );
}

/* ----------------------------------------------------------------------------
 * Patient card — DARK variant. Bold, anchors visual attention.
 * -------------------------------------------------------------------------- */
function PatientCard() {
  const bullets = [
    "Share what living with your condition is really like",
    "Get fairly thanked for each study you join",
    "Take part on your terms: video, survey, journal",
  ];
  return (
    <Link
      to="/patient/sign-up"
      data-testid="register-as-patient"
      className="group relative overflow-hidden rounded-3xl bg-[var(--dark-bg)] text-white p-8 md:p-10 grain-dark transition-transform hover:-translate-y-1 hover:shadow-[0_30px_70px_-30px_rgba(15,20,34,0.6)]"
    >
      {/* Lime glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-28 -right-28 h-72 w-72 rounded-full bg-[var(--lime)]/15 blur-3xl"
      />

      <div className="relative">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[var(--lime)] mb-6">
          <Heart size={26} strokeWidth={2.2} className="text-[var(--dark-bg)]" />
        </div>

        <h3 className="font-['Outfit'] font-bold text-white text-3xl md:text-[34px] tracking-tight leading-[1.1] mb-3">
          I'm a patient
        </h3>
        <p className="text-white/75 text-[15px] leading-relaxed mb-7 max-w-md">
          You live with a medical condition and want your real, lived
          experience to shape better treatments and care.
        </p>

        <ul className="space-y-2.5 mb-9">
          {bullets.map((b) => (
            <li
              key={b}
              className="flex items-start gap-2.5 text-sm text-white/90"
            >
              <span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-[var(--lime)]/15 flex-shrink-0">
                <Check
                  size={12}
                  strokeWidth={3}
                  className="text-[var(--lime)]"
                />
              </span>
              <span>{b}</span>
            </li>
          ))}
        </ul>

        <span className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[var(--lime)] text-[var(--dark-bg)] font-semibold text-sm transition-all group-hover:gap-3 group-hover:shadow-[0_12px_30px_-8px_rgba(196,247,75,0.6)]">
          Register as a patient
          <ArrowRight size={16} strokeWidth={2.5} />
        </span>
      </div>
    </Link>
  );
}

/* ----------------------------------------------------------------------------
 * Caregiver card — LIGHT variant with bone background. Quieter counterpoint.
 * -------------------------------------------------------------------------- */
function CaregiverCard() {
  const bullets = [
    "Share what caregiving really looks like, every day",
    "Help researchers see the people behind the patient",
    "Take part in studies designed around your time",
  ];
  return (
    <Link
      to="/caregiver/sign-up"
      data-testid="register-as-caregiver"
      className="group relative overflow-hidden rounded-3xl bg-[var(--bone-soft)] border border-[var(--light-border)] p-8 md:p-10 transition-all hover:-translate-y-1 hover:border-[var(--charcoal)] hover:shadow-[0_30px_70px_-30px_rgba(15,20,34,0.25)]"
    >
      {/* subtle radial accent in the top-right */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[var(--lime)]/30 blur-3xl"
      />

      <div className="relative">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[var(--charcoal)] mb-6">
          <Users size={26} strokeWidth={2.2} className="text-[var(--lime)]" />
        </div>

        <h3 className="font-['Outfit'] font-bold text-[var(--charcoal)] text-3xl md:text-[34px] tracking-tight leading-[1.1] mb-3">
          I'm a caregiver
        </h3>
        <p className="text-[var(--text-muted)] text-[15px] leading-relaxed mb-7 max-w-md">
          You support a partner, parent, child or friend living with a medical
          condition, and your perspective shapes the research too.
        </p>

        <ul className="space-y-2.5 mb-9">
          {bullets.map((b) => (
            <li
              key={b}
              className="flex items-start gap-2.5 text-sm text-[var(--charcoal)]"
            >
              <span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-[var(--lime)] flex-shrink-0">
                <Check
                  size={12}
                  strokeWidth={3}
                  className="text-[var(--dark-bg)]"
                />
              </span>
              <span>{b}</span>
            </li>
          ))}
        </ul>

        <span className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[var(--charcoal)] text-white font-semibold text-sm transition-all group-hover:gap-3 group-hover:shadow-[0_12px_30px_-8px_rgba(15,20,34,0.4)]">
          Register as a caregiver
          <ArrowRight size={16} strokeWidth={2.5} />
        </span>
      </div>
    </Link>
  );
}
